// Shared bits for the revenue-split report tabs. The API (GET
// /admin/analytics/revenue, /admin/analytics/order-splits) recognises money on
// DELIVERED items only and buckets by IST calendar day / ISO week / month.

export type Granularity = 'day' | 'week' | 'month';

/** Where a customer's payment went. The four shares sum to `customerPaid`. */
export interface Split {
  customerPaid: number;
  sellerShare: number;
  partnerShare: number;
  tax: number;
  companyRevenue: number;
  companyBreakdown: {
    commissionAndFees: number;
    priceMarkup: number;
    deliveryCharges: number;
    discounts: number;
    partnerPayouts: number;
  };
}

export const inr = (n: number) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
export const inr2 = (n: number) =>
  `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Series colours — validated as a set (dataviz validate_palette, light, white
// surface): brand kumkum for the company, then blue / aqua / yellow. Aqua and
// yellow sit under 3:1 on white, so every use carries a visible text label.
export const SPLIT_COLORS = {
  company: '#D12A66',
  seller: '#2a78d6',
  partner: '#1baf7a',
  tax: '#eda100',
} as const;

export const CHART = { line: '#E6E0E3', axis: '#7A7177', hover: '#F6F2F4' };

// ---- IST dates ----------------------------------------------------------------

const IST = 'Asia/Kolkata';

/** Today's calendar date in India, as YYYY-MM-DD. */
export function todayIst(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: IST }).format(new Date());
}

// Pure calendar arithmetic on YYYY-MM-DD strings (UTC-anchored, so no DST/zone drift).
const parse = (d: string) => new Date(`${d}T00:00:00Z`);
const fmt = (d: Date) => d.toISOString().slice(0, 10);
function addDays(d: string, n: number): string {
  const x = parse(d);
  x.setUTCDate(x.getUTCDate() + n);
  return fmt(x);
}

/** Default window per granularity: 30 days, 12 ISO weeks, or 12 months. */
export function defaultRange(g: Granularity): { from: string; to: string } {
  const to = todayIst();
  if (g === 'day') return { from: addDays(to, -29), to };
  if (g === 'week') {
    const monday = addDays(to, -((parse(to).getUTCDay() + 6) % 7));
    return { from: addDays(monday, -7 * 11), to };
  }
  const t = parse(to);
  return { from: fmt(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() - 11, 1))), to };
}

/** Label for a bucket start date returned by the API. */
export function periodLabel(period: string, g: Granularity, long = false): string {
  const d = parse(period);
  if (g === 'month') return d.toLocaleDateString('en-IN', { month: long ? 'long' : 'short', year: 'numeric', timeZone: 'UTC' });
const dayMonth = (x: Date, year = false) =>
    x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC', ...(year ? { year: 'numeric' } : {}) });
  if (g === 'week') return long ? `${dayMonth(d)} – ${dayMonth(parse(addDays(period, 6)), true)}` : `Wk ${dayMonth(d)}`;
  return dayMonth(d, long);
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: IST });
}

// ---- Split bar --------------------------------------------------------------

const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);

/**
 * One horizontal 100% bar of where the customer's money went, with a labelled
 * legend (value + share) so identity never rests on colour alone.
 */
export function SplitBar({ split, compact = false }: { split: Split; compact?: boolean }) {
  const parts = [
    { key: 'company', label: 'Company', value: split.companyRevenue, color: SPLIT_COLORS.company },
    { key: 'seller', label: 'Seller', value: split.sellerShare, color: SPLIT_COLORS.seller },
    { key: 'partner', label: 'Delivery partner', value: split.partnerShare, color: SPLIT_COLORS.partner },
    { key: 'tax', label: 'GST', value: split.tax, color: SPLIT_COLORS.tax },
  ];
  const whole = split.customerPaid;
  return (
    <div>
      <div
        className={`flex w-full gap-[2px] overflow-hidden rounded-[6px] bg-surface-sunk ${compact ? 'h-2.5' : 'h-3.5'}`}
        role="img"
        aria-label={parts.map((p) => `${p.label} ${inr(p.value)}`).join(', ')}
      >
        {whole > 0 &&
          parts
            .filter((p) => p.value > 0)
            .map((p) => (
              <div key={p.key} title={`${p.label}: ${inr2(p.value)}`} style={{ width: `${pct(p.value, whole)}%`, background: p.color }} />
            ))}
      </div>
      <ul className={`mt-3 grid gap-x-5 gap-y-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4'}`}>
        {parts.map((p) => (
          <li key={p.key} className="flex min-w-0 items-start gap-2">
            <span className="mt-[5px] h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: p.color }} aria-hidden />
            <div className="min-w-0">
              <p className="text-[13px] text-ink-500">{p.label}</p>
              <p className="font-tabular text-[14px] font-semibold text-ink-900">
                {inr2(p.value)} <span className="font-normal text-ink-500">· {pct(p.value, whole).toFixed(1)}%</span>
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
